import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise = null;

// Loads the Turnstile script once and shares it between widgets
const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error("Unable to load Cloudflare Turnstile"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

// Cloudflare Turnstile widget. Parent gets the token through onVerify and can call ref.current.reset().
const Turnstile = forwardRef(({ onVerify, onExpire, onError, theme = "light", size = "flexible", className }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbacksRef = useRef({ onVerify, onExpire, onError });
  callbacksRef.current = { onVerify, onExpire, onError };

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.turnstile) window.turnstile.reset(widgetIdRef.current);
    },
  }));

  useEffect(() => {
    let cancelled = false;
    const siteKey = process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY;

    if (!siteKey) {
      console.error("[Turnstile] REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY is not set");
      if (callbacksRef.current.onError) callbacksRef.current.onError();
      return undefined;
    }

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          callback: (token) => callbacksRef.current.onVerify && callbacksRef.current.onVerify(token),
          "expired-callback": () => callbacksRef.current.onExpire && callbacksRef.current.onExpire(),
          "error-callback": () => callbacksRef.current.onError && callbacksRef.current.onError(),
        });
      })
      .catch((err) => {
        console.error("[Turnstile]", err);
        if (!cancelled && callbacksRef.current.onError) callbacksRef.current.onError();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [theme, size]);

  return <div ref={containerRef} className={className} />;
});

export default Turnstile;
